import { mapState, mapActions, mapGetters } from 'vuex';
import stickyConstants from '@/helpers/stickyConstants';
import marketFiltersMobile from '@/components/market/market-filters/market-filters-mobile/market-filters-mobile.vue';
import marketSectionHeader from '@/components/market/market-section-header/market-section-header.vue';
import marketListDesktopHeader from '@/components/market/market-list-desktop-header/market-list-desktop-header.vue';
import marketListItemV2 from '@/components/market/v2/market-list-item-v2/market-list-item-v2.vue';
import headerTagsMixin from '@/mixins/headerTags';
import userAgentMixin from '@/mixins/userAgent';
import loaderMixin from '@/mixins/loader';
import seoMetaMixin from '@/mixins/seoMeta';
import constants from '@/constants';
import { parseQuery } from '@/utils/helpers';
import log from '@/logger';
import buildCanonicalUrl from '@/utils/canonical-url';
import { buildJsonLdTag, jsonLdSanitizerOverride, buildCollectionPageSchema } from '@/utils/json-ld';

const MARKET_LIST_JSON_LD_VMID = 'jsonld-market-list';

const HOMEPAGE_FONT_LOAD_FALLBACK_MS = 1200;

export default {
  mixins: [
    headerTagsMixin,
    userAgentMixin,
    loaderMixin,
    seoMetaMixin,
  ],
  components: {
    marketFiltersMobile,
    marketSectionHeader,
    marketListDesktopHeader,
    marketListItem: marketListItemV2,
  },
  props: {
    notificationText: {
      type: String,
      required: false,
    },
    notificationLevel: {
      type: String,
      required: false,
    },
    headerId: Number,
    headerUrl: String,
    page: {
      type: Number,
      default: 1,
    },
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: true,
      isFiltering: false,
      stickyOptions: stickyConstants.mobile,
      watchersDisabled: false,
      isInfiniteLoading: false,
      isLoadingPrevious: false,
      embeddedInitialFontsReady: false,
      canonicalUrl: null,
      currentDisplayedPage: this.page,
      observedBoundaryEls: new Set(),
    };
  },
  metaInfo() {
    if (this.embedded) return {};

    const isPaymentRoute = this.$route.name === 'payment-success'
      || this.$route.name === 'payment-fail';
    const noindexMeta = isPaymentRoute ? [{ name: 'robots', content: 'noindex,nofollow' }] : [];

    if (this.currentHeader !== null) {
      const canonicalUrl = buildCanonicalUrl(this.$router, 'market-list', {
        headerId: this.currentHeader.headerId,
        headerUrl: this.currentHeader.headerUrl,
      });
      const jsonLdSchema = buildCollectionPageSchema({
        name: this.currentHeader.seoTitle,
        description: this.currentHeader.seoDescription,
        url: canonicalUrl,
        items: this.items.map(market => ({
          name: market.marketName,
          url: buildCanonicalUrl(this.$router, 'market-details', {
            marketId: market.marketId,
            marketUrl: market.marketUrl,
          }),
        })),
      });
      return {
        ...this.buildSeoMeta({
          title: this.currentHeader.seoTitle || 'Markets',
          description: this.currentHeader.seoDescription,
          type: 'website',
          url: canonicalUrl,
          extraMeta: noindexMeta,
        }),
        script: [buildJsonLdTag(jsonLdSchema, MARKET_LIST_JSON_LD_VMID)],
        __dangerouslyDisableSanitizersByTagID: jsonLdSanitizerOverride(MARKET_LIST_JSON_LD_VMID),
      };
    }
    const canonicalUrl = buildCanonicalUrl(this.$router, 'market-list');
    return this.buildSeoMeta({
      title: 'Political Prediction Markets: Trade the Odds | PredictIt',
      description: 'Trade on elections, politics and world events with real-money prediction markets. Explore every PredictIt market and see where the odds stand today.',
      type: 'website',
      url: canonicalUrl,
      extraMeta: noindexMeta,
    });
  },
  computed: {
    ...mapState('browse', [
      'selectedFilters',
      'selectedOrder',
      'selectedTimespan',
      'filteredMarkets',
      'headers',
      'latestHeaderId',
      'marketsPageInitialLoadDone',
      'marketListCachedHeaderId',
    ]),
    ...mapState('user', ['marketDisplay']),
    ...mapGetters('app', ['isLayoutDesktop', 'isLayoutMobile']),
    ...mapGetters('user', ['isLoggedIn']),
    ...mapState('app', ['previousRoute']),
    items() {
      const markets = this.filteredMarkets.data === null ? [] : this.filteredMarkets.data;
      const statuses = [
        constants.MARKET_SETTLED,
        constants.MARKET_EXPIRED,
        constants.MARKET_SUSPENDED,
      ];
      return markets
        .filter(m => !statuses.includes(m.status));
    },
    showNoMarketsMessage() {
      return !this.showInitialSpinner
        && !this.isLoading
        && !this.isInfiniteLoading
        && this.items.length === 0;
    },
    currentHeader() {
      const header = this.headers ? this.headers.find(h => h.headerId === this.headerId) : null;
      return header || null;
    },
    currentHeaderName() {
      const header = this.currentHeader;
      if (header) {
        return header.name;
      }
      return this.selectedFilters.length ? 'Filtered Markets' : 'All Markets';
    },
    filtering() {
      return this.selectedFilters.toString() + this.selectedOrder.name + this.selectedTimespan.name;
    },
    showSpinner() {
      return !this.embedded || (this.embedded && this.marketsPageInitialLoadDone);
    },
    showInitialSpinner() {
      return this.embedded ? (!this.embeddedInitialFontsReady && !this.marketsPageInitialLoadDone) : !this.marketsPageInitialLoadDone;
    },
    marketSkeletonCount() {
      return this.isLayoutDesktop ? 9 : 6;
    },
    nextPageRouteLocation() {
      if (this.embedded || !this.marketsPageInitialLoadDone) return null;
      if (!this.filteredMarkets.canLoadMore) return null;
      return {
        name: 'market-list',
        params: {
          headerId: this.headerId,
          headerUrl: this.headerUrl,
        },
        query: {
          filterIds: this.selectedFilters.join(','),
          sort: this.selectedOrder.sort,
          sortParameter: this.selectedTimespan.parameter,
          ...(this.selectedTimespan.dateFrom && this.selectedTimespan.dateTo ? {
            dateFrom: this.selectedTimespan.dateFrom,
            dateTo: this.selectedTimespan.dateTo,
          } : {}),
          page: (this.filteredMarkets.page || 0) + 1,
        },
      };
    },
  },
  methods: {
    ...mapActions('browse', [
      'clearFilters',
      'clearTimespan',
      'clearOrder',
      'getFilters',
      'getFilteredMarkets',
      'setLatestHeaderId',
      'resetFilteredMarkets',
      'setMarketsPageInitialLoadDone',
      'setMarketListCachedHeaderId',
    ]),
    ...mapActions('user', ['updateMarketDisplay']),
    ...mapActions('notifications', ['setSideNotification']),
    fetchData() {
      this.isLoading = true;
      this.currentDisplayedPage = this.page;
      this.resetBoundaryTracking();
      if (this.embedded) {
        this.embeddedInitialFontsReady = false;
      }
      if (this.isFiltering) return;
      if (Number.isInteger(this.headerId)) {
        this.notifyEmbeddedInitialLoadState(true);
        log.info('[MARKETS] Fetching Data');
        this.isFiltering = true;
        this.reorganizeTags();
        this.getFilters(this.headerId).then(() => {
          this.getMarkets(true).then(() => {
            this.isFiltering = false;
            this.isLoading = false;
            this.setMarketsPageInitialLoadDone(true);
            this.setMarketListCachedHeaderId(this.headerId);
            this.redirectIfPageOutOfRange();
            this.upgradeLegacyTimespanUrl();
            this.waitForEmbeddedInitialFonts()
              .then(() => {
                this.notifyEmbeddedInitialLoadState(false);
              });
          }).catch(() => {
            this.isLoading = false;
            this.isFiltering = false;
            this.setMarketsPageInitialLoadDone(true);
            this.waitForEmbeddedInitialFonts()
              .then(() => {
                this.notifyEmbeddedInitialLoadState(false);
              });
          });
        }).catch((error) => {
          this.isFiltering = false;
          this.isLoading = false;
          this.setMarketsPageInitialLoadDone(true);
          const status = error && error.response && error.response.status;
          if (status === 404) {
            this.$router.push({ name: 'not-found' });
            return;
          }
          this.waitForEmbeddedInitialFonts()
            .then(() => {
              this.notifyEmbeddedInitialLoadState(false);
            });
        });
      }
    },
    getMarkets(reset = false) {
      return this.getFilteredMarkets({
        headerId: this.headerId || 0,
        reset,
        page: reset ? this.page : null,
      });
    },
    loadPrevious() {
      if (this.isLoadingPrevious || !this.filteredMarkets.canLoadPrevious) return;
      this.isLoadingPrevious = true;
      // Prepending shifts every element below the sentinel down, which would
      // otherwise yank the viewport - hold scroll position steady around it.
      const previousScrollHeight = document.documentElement.scrollHeight;
      const previousScrollTop = window.scrollY;
      this.getFilteredMarkets({ headerId: this.headerId || 0, prepend: true })
        .then(() => {
          this.$nextTick(() => {
            const heightDelta = document.documentElement.scrollHeight - previousScrollHeight;
            window.scrollTo(0, previousScrollTop + heightDelta);
            this.isLoadingPrevious = false;
          });
        });
    },
    redirectIfPageOutOfRange() {
      if (this.embedded) return;
      const { data, totalCount, totalPages } = this.filteredMarkets;
      const outOfRange = this.page > 1 && totalCount > 0 && (!data || data.length === 0);
      if (!outOfRange) return;

      const currentQuery = parseQuery(window.location.search || '');
      this.$router.replace({
        name: 'market-list',
        params: {
          headerId: this.headerId,
          headerUrl: this.headerUrl,
        },
        query: {
          ...currentQuery,
          page: Math.max(1, totalPages || 1),
        },
      });
    },
    onLayoutToggle(layout) {
      this.updateMarketDisplay(layout);
    },
    notifyEmbeddedInitialLoadState(isLoading) {
      if (this.embedded) {
        this.$emit('embeddedInitialLoadStateChange', isLoading);
      }
    },
    waitForEmbeddedInitialFonts() {
      if (!this.embedded || this.embeddedInitialFontsReady) {
        this.embeddedInitialFontsReady = true;
        return Promise.resolve();
      }

      if (typeof document === 'undefined' || !document.fonts || typeof document.fonts.load !== 'function') {
        this.embeddedInitialFontsReady = true;
        return Promise.resolve();
      }

      const requiredFontFaces = [
        '500 14px Roboto',
        '300 12px Roboto',
        '300 24px "Roboto Mono"',
      ];

      if (requiredFontFaces.every(fontFace => document.fonts.check(fontFace))) {
        this.embeddedInitialFontsReady = true;
        return Promise.resolve();
      }

      return Promise.race([
        Promise.all([
          ...requiredFontFaces.map(fontFace => document.fonts.load(fontFace)),
        ]),
        new Promise((resolve) => {
          setTimeout(resolve, HOMEPAGE_FONT_LOAD_FALLBACK_MS);
        }),
      ]).catch(() => {})
        .then(() => {
          this.embeddedInitialFontsReady = true;
        });
    },
    filteringChanged() {
      if (this.watchersDisabled) return;

      log.info(`[MARKETS] Filtering. Is in Progress: ${this.isFiltering.toString()}`);
      if (this.isFiltering) return;
      if (this.embedded) {
        this.fetchData();
      } else {
        this.$router.push({
          name: 'market-list',
          params: {
            headerId: this.headerId,
            headerUrl: this.headerUrl,
          },
          query: {
            filterIds: this.selectedFilters.join(','),
            sort: this.selectedOrder.sort,
            sortParameter: this.selectedTimespan.parameter,
            ...(this.selectedTimespan.dateFrom && this.selectedTimespan.dateTo ? {
              dateFrom: this.selectedTimespan.dateFrom,
              dateTo: this.selectedTimespan.dateTo,
            } : {}),
            page: 1,
          },
        });
        this.fetchData();
        this.currentDisplayedPage = 1;
      }
    },
    headerIdChanged(newValue) {
      if (this.embedded) {
        this.resetFilteredMarkets();
        this.setLatestHeaderId(newValue);
        this.clearFilters();
        this.clearOrder();
        this.clearTimespan();
        this.fetchData();
      } else {
        if (
          this.marketsPageInitialLoadDone &&
          this.marketListCachedHeaderId === newValue &&
          this.filteredMarkets.data && this.filteredMarkets.data.length > 0
        ) {
          return;
        }
        this.fetchData();
      }
    },
    loadMore() {
      if (this.isInfiniteLoading || !this.filteredMarkets.canLoadMore) return;
      this.isInfiniteLoading = true;
      log.info(`[MARKETS] load more, initialLoadDone=${this.marketsPageInitialLoadDone}`);
      this.getMarkets(false);
    },
    setupScrollObservers() {
      if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return;

      this.loadMoreObserver = new IntersectionObserver(this.onLoadMoreIntersect, {
        rootMargin: '0px 0px 600px 0px',
        threshold: 0,
      });
      this.observeLoadMoreSentinel();

      // Not an IntersectionObserver: the sentinel sits at the very top of the list, so it's
      // already "intersecting" the instant the page loads - that fired loadPrevious before the
      // user ever touched the scrollbar. Instead only check it on a genuine upward scroll
      window.addEventListener('wheel', this.onWheel, { passive: true });
      window.addEventListener('touchstart', this.onTouchStart, { passive: true });
      window.addEventListener('touchmove', this.onTouchMove, { passive: true });

      if (!this.embedded) {
        this.pageBoundaryObserver = new IntersectionObserver(this.onPageBoundaryIntersect, {
          rootMargin: '-45% 0px -50% 0px',
          threshold: 0,
        });
        this.observePageBoundaries();
      }
    },
    onLoadMoreIntersect(entries) {
      const isVisible = entries.some(entry => entry.isIntersecting);
      if (isVisible && this.marketsPageInitialLoadDone) this.loadMore();
    },
    observeLoadMoreSentinel() {
      if (!this.loadMoreObserver) return;
      this.$nextTick(() => {
        const sentinel = this.$refs.loadMoreSentinel;
        if (sentinel) this.loadMoreObserver.observe(sentinel);
      });
    },
    onWheel(event) {
      if (event.deltaY < 0) this.onScrolledUpward();
    },
    onTouchStart(event) {
      this.touchStartY = event.touches[0].clientY;
    },
    onTouchMove(event) {
      if (this.touchStartY == null) return;
      const currentY = event.touches[0].clientY;
      const swipingDown = currentY > this.touchStartY;
      this.touchStartY = currentY;
      if (swipingDown) this.onScrolledUpward();
    },
    onScrolledUpward() {
      this.checkLoadPrevious();
      this.syncCurrentPageFromBoundaries();
    },
    checkLoadPrevious() {
      if (!this.marketsPageInitialLoadDone) return;
      const sentinel = this.$refs.loadPreviousSentinel;
      if (!sentinel) return;
      const rect = sentinel.getBoundingClientRect();
      if (rect.bottom > 0 && rect.top < window.innerHeight) this.loadPrevious();
    },
    syncCurrentPageFromBoundaries() {
      if (this.embedded || !this.marketsPageInitialLoadDone || !this.$el) return;
      const referenceY = window.innerHeight / 2;
      let primaryPage = null;
      this.$el.querySelectorAll('[data-page-boundary]').forEach((el) => {
        const pageNumber = parseInt(el.getAttribute('data-page-boundary'), 10);
        if (Number.isNaN(pageNumber)) return;
        if (el.getBoundingClientRect().top <= referenceY
          && (primaryPage === null || pageNumber > primaryPage)) {
          primaryPage = pageNumber;
        }
      });
      if (primaryPage === null || primaryPage === this.currentDisplayedPage) return;
      this.currentDisplayedPage = primaryPage;
      this.syncQueryToUrl({ page: primaryPage > 1 ? primaryPage : undefined });
    },
    observePageBoundaries() {
      if (!this.pageBoundaryObserver) return;
      this.$nextTick(() => {
        this.resetBoundaryTracking();
        const boundaryEls = this.$el.querySelectorAll('[data-page-boundary]');
        boundaryEls.forEach((el) => {
          this.observedBoundaryEls.add(el);
          this.pageBoundaryObserver.observe(el);
        });
      });
    },
    // observedBoundaryEls and the observer keep strong references
    // to detached nodes for the rest of the component's lifetime
    resetBoundaryTracking() {
      if (this.pageBoundaryObserver) {
        this.observedBoundaryEls.forEach(el => this.pageBoundaryObserver.unobserve(el));
      }
      this.observedBoundaryEls.clear();
    },
    onPageBoundaryIntersect(entries) {
      const visiblePages = entries
        .filter(entry => entry.isIntersecting)
        .map(entry => parseInt(entry.target.getAttribute('data-page-boundary'), 10))
        .filter(pageNumber => !Number.isNaN(pageNumber));
      if (!visiblePages.length) return;
      const primaryPage = Math.max(...visiblePages);
      if (primaryPage === this.currentDisplayedPage) return;

      clearTimeout(this.syncPageDebounceTimer);
      this.syncPageDebounceTimer = setTimeout(() => {
        this.currentDisplayedPage = primaryPage;
        this.syncQueryToUrl({ page: primaryPage > 1 ? primaryPage : undefined });
      }, 200);
    },
    // not $router.replace, to avoid re-triggering the $route watcher's filter/timespan reset
    syncQueryToUrl(overrides) {
      const currentQuery = parseQuery(window.location.search || '');
      const resolvedRoute = this.$router.resolve({
        name: 'market-list',
        params: {
          headerId: this.headerId,
          headerUrl: this.headerUrl,
        },
        query: { ...currentQuery, ...overrides },
      });
      window.history.replaceState(window.history.state, document.title, resolvedRoute.href);
      this.canonicalUrl = window.location.href;
    },
    // Self-heals legacy/bare links (?sortParameter=WEEK with no dates) once the
    // range is resolved, so shared/indexed URLs become absolute going forward.
    upgradeLegacyTimespanUrl() {
      if (this.embedded) return;
      const { dateFrom, dateTo } = this.selectedTimespan;
      if (!dateFrom || !dateTo) return;
      if (this.$route.query.dateFrom === dateFrom && this.$route.query.dateTo === dateTo) return;
      this.syncQueryToUrl({ dateFrom, dateTo });
    },
  },
  watch: {
    $route: {
      handler(to, from) {
        this.canonicalUrl = window.location.href;
        const targetHeaderId = parseInt(to.params && to.params.headerId, 10) || 0;
        const previousHeaderId = from
          ? (parseInt(from.params && from.params.headerId, 10) || 0)
          : null;
        const prevRoute = this.previousRoute || '';
        const returningFromMarketDetails = prevRoute.startsWith('/markets/detail/')
          && this.marketsPageInitialLoadDone
          && this.marketListCachedHeaderId === targetHeaderId
          && this.filteredMarkets.data && this.filteredMarkets.data.length > 0;

        if (returningFromMarketDetails) return;

        this.watchersDisabled = true;
        this.setMarketsPageInitialLoadDone(false);
        this.clearFilters();
        this.clearOrder();
        this.clearTimespan();

        if (to.query.filterIds) {
          to.query.filterIds.split(',').forEach((i) => {
            const filterId = parseInt(i, 10);
            if (filterId) this.$store.dispatch('browse/addFilter', filterId);
          });
        }
        if (to.query.sortParameter) {
          this.$store.dispatch('browse/setTimespanFromQuery', {
            parameter: to.query.sortParameter,
            dateFrom: to.query.dateFrom,
            dateTo: to.query.dateTo,
          });
        }
        if (to.query.sort) {
          this.$store.dispatch('browse/setOrderFromQuery', to.query.sort);
        }
        this.upgradeLegacyTimespanUrl();

        this.$nextTick(() => { this.watchersDisabled = false; });

        // to avoid firing two overlapping fetches for the same navigation.
        const headerIdChanging = previousHeaderId !== null && targetHeaderId !== previousHeaderId;
        if (from && !headerIdChanging && !this.isFiltering && !this.embedded) {
          const targetPage = Math.max(1, parseInt(to.query.page, 10) || 1);
          if (targetPage !== this.currentDisplayedPage) {
            this.currentDisplayedPage = targetPage;
            this.resetBoundaryTracking();
            this.getFilteredMarkets({ headerId: targetHeaderId, reset: true, page: targetPage })
              .then(() => {
                this.setMarketsPageInitialLoadDone(true);
                this.redirectIfPageOutOfRange();
              });
          }
        }
      },
      immediate: true,
    },
    headerId: {
      handler: 'headerIdChanged',
      immediate: true,
    },
    filtering: 'filteringChanged',
    isLoggedIn: 'fetchData',
    items() {
      this.isInfiniteLoading = false;
      this.observeLoadMoreSentinel();
      this.observePageBoundaries();
    },
    // the first observe() call can run before the sentinel exists in the DOM
    marketsPageInitialLoadDone(isDone) {
      if (!isDone) return;
      this.observeLoadMoreSentinel();
      this.observePageBoundaries();
    },
  },
  created() {
    this.canonicalUrl = window.location.href;
    if (this.notificationText && this.notificationLevel) {
      this.setSideNotification({
        text: this.notificationText,
        level: this.notificationLevel,
      });
    }
  },
  mounted() {
    this.setupScrollObservers();
  },
  beforeDestroy() {
    if (this.loadMoreObserver) this.loadMoreObserver.disconnect();
    window.removeEventListener('wheel', this.onWheel);
    window.removeEventListener('touchstart', this.onTouchStart);
    window.removeEventListener('touchmove', this.onTouchMove);
    if (this.pageBoundaryObserver) this.pageBoundaryObserver.disconnect();
    clearTimeout(this.syncPageDebounceTimer);
    if (this.embedded) {
      this.watchersDisabled = true;
      this.resetFilteredMarkets();
      this.clearFilters();
      this.clearOrder();
      this.clearTimespan();
    }
  },
};



// WEBPACK FOOTER //
// ./src/components/pages/page-markets/page-markets.js